import { useState } from 'react';
import { toISODateString } from '@chainflip/utils/date';
import { isNotNullish } from '@chainflip/utils/guard';
import { type UTCDate } from '@date-fns/utc';
import { useMutation } from '@tanstack/react-query';
import { differenceInSeconds } from 'date-fns';
import { secondsInYear } from 'date-fns/constants';
import { fundingEventLabelMap } from '@/shared/components/atoms/FundingEventBadge';
import { DashboardModalWithCloseButton, ModalContent } from '@/shared/components/dashboards/Modal';
import Button from '@/shared/components/flip-ui-kit/Button';
import { PeriodSelector } from '@/shared/components/PeriodSelector';
import { processorClient } from '@/shared/graphql/client';
import { getFundingEventsQuery } from '@/shared/graphql/explorer/account';
import { type AccountFundingEventFragment } from '@/shared/graphql/generated/explorer/graphql';
import useToast from '@/shared/hooks/useToast';
import { timeframes } from '@/shared/utils/date';
import { exportXlsx, type Sheet } from '@/shared/utils/exportXlsx';
import { fetchAllPages } from '@/shared/utils/fetchAllPages';
import { formatPct } from '@/shared/utils/strings';
import TokenAmount from '@/shared/utils/TokenAmount';
import { getEventLabel } from './DelegateActivityTable';
import {
  getDelegationActivityQuery,
  getDelegationHistoryQuery,
} from '../../../graphql/explorer/delegatorQueries';

const getFundingTransactionLabel = ({
  txHash,
  swapRequest,
  accountChannel,
}: AccountFundingEventFragment) => {
  if (txHash) return txHash;
  if (swapRequest) return `Swap #${swapRequest.nativeId}`;
  if (accountChannel) {
    return `Channel ${accountChannel.issuedBlockId}-${accountChannel.chain}-${accountChannel.channelId}`;
  }
  return '';
};

const allTimeTimeframe = timeframes[timeframes.length - 1];

export default function ExportHistoryModal({
  delegatorIdSs58,
  open,
  onClose,
}: {
  delegatorIdSs58: string;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast('export-history');
  const [startDate, setStartDate] = useState<UTCDate>(allTimeTimeframe.startDate);
  const [endDate, setEndDate] = useState<UTCDate>(allTimeTimeframe.endDate);
  const [selectedDates, setSelectedDates] = useState<UTCDate[]>([
    allTimeTimeframe.startDate,
    allTimeTimeframe.endDate,
  ]);

  const { mutate: exportHistory, isPending } = useMutation({
    mutationFn: async () => {
      const isInRange = (timestamp: string) => {
        const time = new Date(timestamp).getTime();
        return time >= startDate.getTime() && time <= endDate.getTime();
      };

      const [delegationActivities, fundingEvents, delegationHistory] = await Promise.all([
        fetchAllPages(async (first, offset) => {
          const data = await processorClient.request(getDelegationActivityQuery, {
            delegationIdSs58: delegatorIdSs58,
            first,
            offset,
          });

          return {
            nodes: data.allDelegationActivities?.edges.map((edge) => edge.node) ?? [],
            totalCount: data.allDelegationActivities?.totalCount ?? 0,
          };
        }),
        fetchAllPages(async (first, offset) => {
          const data = await processorClient.request(getFundingEventsQuery, {
            idSs58s: [delegatorIdSs58],
            first,
            offset,
          });

          return {
            nodes: data.events?.edges.map((edge) => edge.node) ?? [],
            totalCount: data.events?.totalCount ?? 0,
          };
        }),
        fetchAllPages(async (first, offset) => {
          const data = await processorClient.request(getDelegationHistoryQuery, {
            delegationIdSs58: delegatorIdSs58,
            first,
            offset,
          });

          return {
            nodes: data.allOperatorEpochDelegations?.edges.map((edge) => edge.node) ?? [],
            totalCount: data.allOperatorEpochDelegations?.totalCount ?? 0,
          };
        }),
      ]);

      const delegationActivitySheet: Sheet = {
        name: 'Delegation Activity',
        headers: ['Type', 'Amount (FLIP)', 'USD Value', 'Transaction', 'Timestamp'],
        rows: delegationActivities
          .filter((activity) => isInRange(activity.event.block.timestamp))
          .map((activity) => {
            const amount = TokenAmount.fromAsset(activity.amount, 'Flip');

            return {
              Type: getEventLabel(activity.type, amount).text,
              'Amount (FLIP)': Number(amount.toFixed()),
              'USD Value': isNotNullish(activity.valueUsd)
                ? Number(Number(activity.valueUsd).toFixed(2))
                : '',
              Transaction: activity.txHash ?? '',
              Timestamp: activity.event.block.timestamp,
            };
          }),
      };

      const fundingActivitySheet: Sheet = {
        name: 'Account Funding Activity',
        headers: ['Type', 'Amount (FLIP)', 'Transaction', 'Destination or Source', 'Timestamp'],
        rows: fundingEvents
          .filter((fundingEvent) => isInRange(fundingEvent.event.block.timestamp))
          .map((fundingEvent) => ({
            Type: fundingEventLabelMap[fundingEvent.type],
            'Amount (FLIP)': Number(TokenAmount.fromAsset(fundingEvent.amount, 'Flip').toFixed()),
            Transaction: getFundingTransactionLabel(fundingEvent),
            'Destination or Source': fundingEvent.rebalanceCounterparty?.idSs58 ?? '',
            Timestamp: fundingEvent.event.block.timestamp,
          })),
      };

      const delegationHistorySheet: Sheet = {
        name: 'Delegation History',
        headers: [
          'Authority Set',
          'Operator',
          'Operator Address',
          'Delegated Amount (FLIP)',
          'Delegated USD Value',
          'Rewards (FLIP)',
          'Rewards USD Value',
          'APY',
          'Start Date',
          'End Date',
        ],
        rows: delegationHistory
          .filter(({ operatorEpoch: { epoch } }) => {
            const epochStart = new Date(epoch.startBlock.timestamp).getTime();
            const epochEnd = epoch.endBlock
              ? new Date(epoch.endBlock.timestamp).getTime()
              : Date.now();

            return epochStart <= endDate.getTime() && epochEnd >= startDate.getTime();
          })
          .map(
            ({
              amount: rawAmount,
              rewards: rawRewards,
              valueUsd,
              rewardsValueUsd,
              operatorEpoch: { epoch, operator },
            }) => {
              const amount = TokenAmount.fromAsset(rawAmount, 'Flip');
              const rewards = TokenAmount.fromAsset(rawRewards, 'Flip');
              const epochDurationSeconds = differenceInSeconds(
                epoch.endBlock?.timestamp ?? new Date(),
                epoch.startBlock.timestamp,
              );
              const apy =
                epochDurationSeconds > 0 && amount.gt(0)
                  ? formatPct(
                      rewards
                        .div(amount.toBigNumber())
                        .div(epochDurationSeconds)
                        .mul(secondsInYear)
                        .toNumber(),
                    )
                  : 'N/A';

              return {
                'Authority Set': epoch.id,
                Operator: operator.account.alias ?? '',
                'Operator Address': operator.account.idSs58,
                'Delegated Amount (FLIP)': Number(amount.toFixed()),
                'Delegated USD Value': isNotNullish(valueUsd)
                  ? Number(Number(valueUsd).toFixed(2))
                  : '',
                'Rewards (FLIP)': Number(rewards.toFixed()),
                'Rewards USD Value': isNotNullish(rewardsValueUsd)
                  ? Number(Number(rewardsValueUsd).toFixed(2))
                  : '',
                APY: apy,
                'Start Date': epoch.startBlock.timestamp,
                'End Date': epoch.endBlock?.timestamp ?? '',
              };
            },
          ),
      };

      exportXlsx(
        `${delegatorIdSs58}-history-${toISODateString(startDate)}-${toISODateString(endDate)}.xlsx`,
        [delegationActivitySheet, fundingActivitySheet, delegationHistorySheet],
      );
    },
    onSuccess: onClose,
    onError: () => {
      toast.error('Failed to export history', {
        description: 'Something went wrong while fetching your history. Please try again.',
      });
    },
  });

  return (
    <DashboardModalWithCloseButton open={open} onClose={onClose}>
      <ModalContent
        title="Export history"
        description="Download your delegation activity, account funding activity and delegation history as an Excel file."
        buttons={
          <Button
            onClick={() => exportHistory()}
            loading={isPending && 'iconOnly'}
            disabled="ifLoading"
          >
            Export
          </Button>
        }
      >
        <div className="flex flex-col gap-y-2">
          <span className="font-aeonikMedium text-14 text-cf-light-3">Timeframe</span>
          <PeriodSelector
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            buttonSize="medium"
          />
        </div>
      </ModalContent>
    </DashboardModalWithCloseButton>
  );
}
